import { userAtom } from "@/Atoms";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import ShareButtons from "@/components/parts/ShareButtons";
import {
  getContentDetailBySlug,
  trackContentAccessCounting,
} from "@/services/api";
import { CONTENT_TYPES, cleanHTML } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import { fitIn } from "@/utils/imageHandler";
import {
  ArrowSquareOut,
  ArrowUpRight,
  CaretDown,
  CaretUp,
  MagnifyingGlassPlus,
  Spinner,
  X,
} from "@phosphor-icons/react";
import { useAtomValue } from "jotai";
import Head from "next/head";
import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Lightbox from "yet-another-react-lightbox";
import { Zoom } from "yet-another-react-lightbox/plugins";
import "yet-another-react-lightbox/styles.css";

// Only slides this close to the active one render their content; the rest
// are empty full-height sections, so a long list stays cheap to scroll.
const RENDER_RADIUS = 2;
// Wait for the scroll to settle before touching the URL, so a fast swipe
// past several items doesn't log a page view for each of them.
const URL_SYNC_DELAY = 500;
// An item counts as viewed (for metering and "Most Viewed") only after it
// stays on screen this long.
const VIEW_COUNT_DELAY = 1000;

// Full-screen, one-item-per-scroll viewer over the /infobytes grid. It reads
// the grid's own list and loads more through the grid's own pager, so the
// order always matches the current search, sort and topics.
export default function InfobyteViewer({
  items,
  total,
  slug,
  hasMore,
  isLoading,
  onLoadMore,
  onSlugChange,
  onClose,
}) {
  const user = useAtomValue(userAtom);
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      items.findIndex((item) => item.slug === slug),
      0
    )
  );
  const [details, setDetails] = useState({});
  const requestedRef = useRef(new Set());
  const countedRef = useRef(new Set());
  const [zoomOpen, setZoomOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const startIndexRef = useRef(activeIndex);

  const active = items[activeIndex];
  const activeDetail = active ? details[active.slug] : null;

  // Close if the item isn't (or is no longer) in the list, e.g. the list was
  // reloaded by a new search.
  const slugMissing = !items.some((item) => item.slug === slug);
  useEffect(() => {
    if (slugMissing && !isLoading) onClose();
  }, [slugMissing, isLoading]);

  // Lock the page behind the overlay. The grid keeps its scroll position.
  useEffect(() => {
    const html = document.documentElement;
    const previous = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      html.style.overflow = previous;
    };
  }, []);

  // Start on the clicked item, before the first paint.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = activeIndex * scroller.clientHeight;
    scroller?.focus({ preventScroll: true });
  }, []);

  // The slide that fills most of the screen is the active one.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveIndex(Number(entry.target.dataset.index));
          }
        });
      },
      { root: scroller, threshold: 0.6 }
    );
    scroller
      .querySelectorAll("[data-index]")
      .forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [items.length]);

  const goTo = (index) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const clamped = Math.min(Math.max(index, 0), items.length);
    scroller.scrollTo({
      top: clamped * scroller.clientHeight,
      behavior: "smooth",
    });
  };

  // Keyboard: one item per key press, Esc closes (unless zoomed in).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (zoomOpen) return;
      if (e.key === "Escape") {
        onClose();
      } else if (["ArrowDown", "PageDown", "j"].includes(e.key)) {
        e.preventDefault();
        goTo(activeIndex + 1);
      } else if (["ArrowUp", "PageUp", "k"].includes(e.key)) {
        e.preventDefault();
        goTo(activeIndex - 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, zoomOpen, items.length]);

  useEffect(() => {
    if (activeIndex !== startIndexRef.current) setHasScrolled(true);
  }, [activeIndex]);

  // Keep the address bar on the active item.
  useEffect(() => {
    if (!active || active.slug === slug) return;
    const timer = setTimeout(() => onSlugChange(active.slug), URL_SYNC_DELAY);
    return () => clearTimeout(timer);
  }, [active?.slug]);

  // The list API has no full image or description, so fetch details for the
  // active item and its neighbours ahead of time.
  useEffect(() => {
    for (let i = activeIndex - 1; i <= activeIndex + 2; i++) {
      const item = items[i];
      if (!item || requestedRef.current.has(item.slug)) continue;
      requestedRef.current.add(item.slug);
      getContentDetailBySlug(item.slug, {
        params: { contentTypeName: CONTENT_TYPES.INFOBYTES },
      })
        .then((response) => {
          const detail = response.data.data;
          // Warm the browser cache so the swap from thumbnail is instant.
          if (detail?.image_url) new window.Image().src = fullImage(detail);
          setDetails((prev) => ({ ...prev, [item.slug]: detail }));
        })
        .catch(() => requestedRef.current.delete(item.slug));
    }
  }, [activeIndex, items]);

  // Load the grid's next page before reaching the end.
  useEffect(() => {
    if (hasMore && !isLoading && activeIndex >= items.length - 3) onLoadMore();
  }, [activeIndex, items.length, hasMore, isLoading]);

  // Same metering call as the detail page.
  useEffect(() => {
    if (!active || countedRef.current.has(active.slug)) return;
    if (user?.roles === "admin-team" || user?.roles === "read-access") return;
    const timer = setTimeout(() => {
      countedRef.current.add(active.slug);
      trackContentAccessCounting(active.slug, {
        ip_address: localStorage.getItem("lctd-ia"),
        user_id: user?.id,
        user_email: user?.email,
      }).catch(() => {});
    }, VIEW_COUNT_DELAY);
    return () => clearTimeout(timer);
  }, [active?.slug, user]);

  const progress = total ? ((activeIndex + 1) / total) * 100 : 0;

  // Portalled to <body> so it sits above the sticky header, while dialogs
  // opened from inside it (e.g. login from the bookmark button) portal in
  // later and stack above it.
  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-label='Infobytes viewer'
      className='fixed inset-0 z-50 bg-[#08070d]/95 backdrop-blur-xl animate-in fade-in duration-300'
    >
      {active && (
        <Head>
          <title>{active.meta_title || active.title}</title>
        </Head>
      )}

      {/* Top bar */}
      <div className='absolute inset-x-0 top-0 z-20 pointer-events-none'>
        <div className='h-0.5 bg-white/5'>
          <div
            className='h-full transition-[width] duration-500 ease-out bg-gradient-to-r from-orange-1 to-red-1'
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className='flex items-center justify-between h-14 px-4 lg:h-16 lg:px-6 bg-gradient-to-b from-[#08070d] to-transparent'>
          <p className='flex items-baseline gap-3 pointer-events-auto'>
            <span className='text-base font-semibold tracking-tight font-display'>
              Infobytes
            </span>
            {active && total > 0 && (
              <span className='text-xs tabular-nums text-white/40'>
                {Math.min(activeIndex + 1, total)} / {total}
              </span>
            )}
          </p>
          <div className='flex items-center gap-2 pointer-events-auto'>
            {active && (
              <Link
                href={`/infobytes/${active.slug}`}
                className='items-center hidden h-9 gap-1.5 px-3 text-xs font-medium transition-colors rounded-full sm:inline-flex text-white/70 hover:text-white bg-white/5 hover:bg-white/10'
              >
                Full page
                <ArrowSquareOut weight='bold' />
              </Link>
            )}
            <button
              type='button'
              onClick={onClose}
              aria-label='Close'
              className='inline-flex items-center justify-center transition-colors rounded-full size-9 bg-white/10 hover:bg-white/20'
            >
              <X weight='bold' />
            </button>
          </div>
        </div>
      </div>

      {/* Slides */}
      <div
        ref={scrollerRef}
        tabIndex={-1}
        className='absolute inset-0 overflow-y-auto outline-none snap-y snap-mandatory overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      >
        {items.map((item, index) => (
          <section
            key={item.uuid}
            data-index={index}
            className='h-full snap-start snap-always'
          >
            {Math.abs(index - activeIndex) <= RENDER_RADIUS && (
              <Slide
                item={item}
                detail={details[item.slug]}
                onZoom={() => setZoomOpen(true)}
              />
            )}
          </section>
        ))}
        {/* Observed like a slide, so landing here (e.g. after a fast fling)
            still triggers loading the next page. */}
        <section
          data-index={items.length}
          className='flex flex-col items-center justify-center h-full gap-4 px-6 text-center snap-start'
        >
          {hasMore ? (
            <Spinner className='text-2xl animate-spin text-white/50' />
          ) : (
            <>
              <p className='text-2xl font-semibold tracking-tight font-display'>
                You&rsquo;re all caught up
              </p>
              <p className='text-sm text-white/50'>
                That&rsquo;s every infobyte in this list.
              </p>
              <button
                type='button'
                onClick={onClose}
                className='h-10 px-5 mt-2 text-sm font-medium transition-colors bg-white rounded-full text-background hover:bg-white/90'
              >
                Back to all Infobytes
              </button>
            </>
          )}
        </section>
      </div>

      {/* Up / down */}
      <div className='absolute z-20 flex-col hidden gap-2 -translate-y-1/2 right-6 top-1/2 lg:flex'>
        <NavButton
          label='Previous infobyte'
          disabled={activeIndex === 0}
          onClick={() => goTo(activeIndex - 1)}
        >
          <CaretUp weight='bold' />
        </NavButton>
        <NavButton
          label='Next infobyte'
          disabled={activeIndex >= items.length}
          onClick={() => goTo(activeIndex + 1)}
        >
          <CaretDown weight='bold' />
        </NavButton>
      </div>

      {/* First-time hint, gone after the first scroll. */}
      {!hasScrolled && activeIndex < items.length - 1 && (
        <div className='absolute z-20 flex items-center gap-2 px-3 py-1.5 text-xs -translate-x-1/2 rounded-full pointer-events-none bottom-5 left-1/2 bg-white/10 backdrop-blur text-white/70 animate-in fade-in slide-in-from-bottom-2 duration-700 delay-500 fill-mode-both'>
          <CaretDown weight='bold' className='animate-nudge-down' />
          Scroll for the next infobyte
        </div>
      )}

      {activeDetail?.image_url && (
        <Lightbox
          open={zoomOpen}
          close={() => setZoomOpen(false)}
          slides={[
            { src: activeDetail.image_url, alt: activeDetail.image_alt },
          ]}
          plugins={[Zoom]}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
        />
      )}
    </div>,
    document.body
  );
}

function fullImage(detail) {
  return fitIn(detail.image_url, 1500);
}

function Slide({ item, detail, onZoom }) {
  // Show the grid thumbnail straight away, then swap in the full image.
  const src = detail?.image_url
    ? fullImage(detail)
    : fitIn(item.thumbnail_url, 600);
  const authors = detail?.authors || item.authors || [];
  const url = `${process.env.BASE_URL}infobytes/${item.slug}`;

  return (
    <article className='flex flex-col h-full max-w-[1400px] gap-5 px-4 pt-16 pb-5 mx-auto lg:flex-row lg:items-center lg:gap-12 lg:pl-10 lg:pr-24 lg:pt-20 lg:pb-10'>
      <div className='relative flex items-center justify-center flex-shrink-0 lg:flex-1 lg:min-h-0 lg:h-full'>
        <button
          type='button'
          onClick={onZoom}
          disabled={!detail?.image_url}
          aria-label='Zoom image'
          className='relative max-w-full max-h-full group'
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={detail?.image_alt || item.thumbnail_alt || item.title}
            className='block object-contain max-w-full max-h-[55dvh] lg:max-h-[calc(100dvh-7.5rem)] rounded-xl shadow-2xl shadow-black/60 ring-1 ring-white/10'
          />
          {detail?.image_url && (
            <span className='absolute inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-opacity rounded-full opacity-0 bottom-3 right-3 bg-black/60 backdrop-blur group-hover:opacity-100'>
              <MagnifyingGlassPlus weight='bold' />
              Zoom
            </span>
          )}
        </button>
      </div>

      {/* Scrolls on its own. On touch, scrolling past its end carries on to
          the next slide; on desktop it stays put so the wheel can read it. */}
      <aside className='flex-1 min-h-0 overflow-y-auto lg:flex-none lg:overscroll-contain lg:max-h-full lg:w-[360px] lg:py-2 [scrollbar-width:thin]'>
        <p className='text-[11px] font-medium tracking-wider uppercase text-white/40'>
          {[item.topic?.name, formatDate(item.publish_date, "DD MMM YYYY")]
            .filter(Boolean)
            .join("  ·  ")}
        </p>
        <h2 className='mt-2 text-xl font-semibold leading-tight tracking-tight lg:text-2xl font-display'>
          {item.title}
        </h2>

        <div className='flex items-center gap-3 pb-4 mt-4 border-b border-white/10'>
          <ShareButtons
            title={item.title}
            description={item.meta_desc}
            url={url}
          />
          <span className='w-px h-5 bg-white/10' />
          <ContentWishlistComponent
            item={item}
            content_type_name_prop={item.content_type?.name}
          />
        </div>

        {detail ? (
          <>
            {detail.description && (
              <div className='mt-4 prose-sm prose prose-invert max-w-none text-white/70 prose-p:my-2'>
                {cleanHTML(detail.description)}
              </div>
            )}
            {authors.length > 0 && (
              <p className='mt-4 text-xs text-white/40'>
                By{" "}
                {authors.map((author, index) => (
                  <span key={author.uuid || index}>
                    {index > 0 &&
                      (index === authors.length - 1 ? " and " : ", ")}
                    <Link
                      href={`/author/${author.slug}`}
                      className='text-white/70 hover:text-white hover:underline'
                    >
                      {author.name}
                    </Link>
                  </span>
                ))}
              </p>
            )}
            {detail.connected_with && (
              <Link
                href={`/reports/${detail.connected_with.slug}`}
                className='flex items-center gap-3 p-3 mt-5 transition-colors border rounded-xl group border-white/10 bg-white/[0.03] hover:bg-white/[0.07]'
              >
                {detail.connected_with.cover_image_url && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={fitIn(detail.connected_with.cover_image_url, 160)}
                    alt=''
                    className='flex-shrink-0 object-cover w-10 rounded aspect-[3/4]'
                  />
                )}
                <span className='flex-1 min-w-0'>
                  <span className='block text-[10px] font-medium tracking-wider uppercase text-white/40'>
                    Related report
                  </span>
                  <span className='block mt-0.5 text-sm font-medium leading-snug line-clamp-2'>
                    {detail.connected_with.title}
                  </span>
                </span>
                <ArrowUpRight
                  weight='bold'
                  className='flex-shrink-0 transition-transform text-white/40 group-hover:-translate-y-0.5 group-hover:translate-x-0.5'
                />
              </Link>
            )}
          </>
        ) : (
          <div className='mt-5 space-y-2.5 animate-pulse'>
            <div className='h-3 rounded bg-white/10' />
            <div className='h-3 rounded bg-white/10' />
            <div className='w-2/3 h-3 rounded bg-white/10' />
          </div>
        )}
      </aside>
    </article>
  );
}

function NavButton({ label, disabled, onClick, children }) {
  return (
    <button
      type='button'
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className='inline-flex items-center justify-center transition-colors rounded-full size-11 bg-white/10 backdrop-blur hover:bg-white/20 disabled:opacity-30 disabled:pointer-events-none'
    >
      {children}
    </button>
  );
}
